import { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Table, Pagination,
} from 'rsuite';
import './EmployeeListSummary.scss';
import LockIcon from '@material-ui/icons/Lock';
import { useAvailableHeight } from '@commonClient2/hooks';
// eslint-disable-next-line import/no-unresolved
import { EmptyTableText, ColumnFilter } from '@commonClient2/components';
import { dynamicSort } from '@commonClient2/services/sort';
import { UPDATE_MANAGER_APP_DATA } from '../../../store/appData';
import EmployeeDataviewTooltip from '../../../../components/EmployeeDataviewTooltip';
import { BidProcessType } from '../../../../Enums';
import {
    defaultSortType, employeeNameDataKey, rankDataKey, sequentialEmployeeTurn, getSortPathByKey,
} from '../../../services/EmployeeSortingHelper';
import { mapToEmployeeListSummaryRow, sequentialScheduleStartDate } from './EmployeeListSummary.service';

const { Column, HeaderCell, Cell } = Table;

const paginationHeight = 65;
const pageSizeOptions = [10, 25, 50, 100, 200];
const initialPageSize = 10;

function EmployeeSelectionSummary() {
    const dispatch = useDispatch();
    const ref = useRef(null);
    const tableHeight = (useAvailableHeight(ref) ?? 400) - paginationHeight;

    /** @type {ShiftBidding.ManagerStore} */
    // @ts-ignore
    const { bidProfileDraft, bidProfile, managerAppData } = useSelector((state) => state);
    const { dataviewEmployeeData, dataviewFields } = managerAppData;

    /**
     * @type {BiddingDraft.IBidProfileDraft | BidProfile.IBidProfile | undefined}
     */
    let profile;
    if (bidProfileDraft.id !== '') {
        profile = bidProfileDraft;
    } else if (bidProfile.id !== '') {
        profile = bidProfile;
    }

    if (profile === undefined) {
        return <></>;
    }

    /**
     * @type {{[Key: string]: number}}
     */
    const rankedEmployees = {};
    for (const rankItem of profile.originalEmployees.rankedEmployees) {
        rankedEmployees[rankItem.employeeId] = rankItem.rank;
    }

    const isSequential = profile.originalBidProfileInformation.bidProcessType === BidProcessType.Sequential;
    const { sequentialSchedule } = profile.originalSchedule;

    const [sortType, setSortType] = useState(defaultSortType);
    const [sortColumn, setSortColumn] = useState(rankDataKey);
    const [pageSize, setPageSize] = useState(initialPageSize);
    const [pageIndex, setPageIndex] = useState(1);

    /**
     * @param {ManagerAppData.IEmployee[]} dataviewEmployees
     * @returns {BidProfile.IEmployeeListSummaryRow[]}
     */
    const formatAndSortEmployees = (dataviewEmployees) => dataviewEmployees
        .filter((x) => profile?.originalEmployees.employeesIds?.some((item) => item === x.employeeId))
        .map((x) => mapToEmployeeListSummaryRow(x, rankedEmployees, isSequential, sequentialSchedule, profile?.originalEmployees.lockedEmployeeIds));

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [employees, setEmployees] = useState(formatAndSortEmployees(dataviewEmployeeData));

    const paginatedData = employees.filter((v, i) => {
        const start = pageSize * (pageIndex - 1);
        const end = start + pageSize;
        return i >= start && i < end;
    });

    /** @param {{rowData: ManagerAppData.IEmployee, dataKey: string}} row */
    const NameCell = ({ rowData, ...props }) => { // TODO KAT 06/16: What fields needs to be displayed in tooltip?
        const row = dataviewEmployeeData.find((x) => x.employeeId === rowData.employeeId);
        if (!row) {
            return <></>;
        }

        return (
            <Cell {...props}>
                <EmployeeDataviewTooltip
                    key={row.employeeName}
                    employeeName={row.employeeName}
                    dataviewAttributes={row.dataviewAttributes}
                />
            </Cell>
        );
    };

    /**
     * @param {string[]} selectedColumns
     */
    const handleDataviewFilterChange2 = (selectedColumns) => {
        const newColumnSelection = [...dataviewFields].map((x) => ({ ...x, checked: selectedColumns.includes(x.value) }));

        dispatch({
            type: UPDATE_MANAGER_APP_DATA,
            payload: { dataviewFields: newColumnSelection },
        });
    };

    /**
     * @param {string} newSortColumn
     * @param {CommonClient2.SortType} newSortType
     */
    const handleSortColumn = (newSortColumn, newSortType) => {
        setSortColumn(newSortColumn);
        setSortType(newSortType);
    };

    useEffect(() => {
        const employeeListSummaryRows = formatAndSortEmployees(dataviewEmployeeData);
        const sortPath = getSortPathByKey(employeeListSummaryRows, sortColumn);
        const isDescending = sortType === 'desc';
        if (sortPath === sequentialEmployeeTurn) {
            const sortKey = isDescending ? `-${sequentialScheduleStartDate}` : `${sequentialScheduleStartDate}`;
            employeeListSummaryRows?.sort(dynamicSort(sortKey));
        } else {
            const sortKey = isDescending ? `-${sortPath}` : sortPath;
            employeeListSummaryRows?.sort(dynamicSort(sortKey));
        }
        setEmployees(employeeListSummaryRows);
    }, [JSON.stringify(profile.originalSchedule.sequentialSchedule), JSON.stringify(dataviewEmployeeData), sortType, sortColumn]);

    /**
     * @param {number} size 
     */
    const handleChangePageSize = (size) => {
        setPageIndex(1);
        setPageSize(size);
    };

    /**
     * @param {number} pageNumber 
     */
    const handleChangePageIndex = (pageNumber) => {
        setPageIndex(pageNumber);
    };

    return (
        <div className="summary-section-right">
            <div
                id="employeeSummarySectionHeader"
                className="selected-employees-column-filter-wrapper"
            >
                <ColumnFilter
                    options={managerAppData.dataviewFields}
                    onOptionSelected={handleDataviewFilterChange2}
                />
            </div>
            <div
                ref={ref}
                className="table-container"
            >
                <Table
                    height={tableHeight}
                    data={paginatedData}
                    sortType={sortType}
                    sortColumn={sortColumn}
                    onSortColumn={handleSortColumn}
                    renderEmpty={() => <EmptyTableText text="No employee found" />}
                    id="tableListEmployeeSummary"
                >
                    <Column
                        width={50}
                        align="center"
                        resizable
                        sortable
                    >
                        <HeaderCell style={{ padding: 0 }}>
                            <div style={{ lineHeight: '40px' }} />
                        </HeaderCell>
                        <Cell dataKey={rankDataKey}>
                            {/** @param {BidProfile.IEmployeeListSummaryRow} rowData */
                                // @ts-ignore
                                (rowData) => (rowData.isLocked
                                    ? <LockIcon />
                                    : rowData.rank)
                            }
                        </Cell>
                    </Column>

                    <Column
                        width={160}
                        resizable
                        sortable
                    >
                        <HeaderCell>Employee Name</HeaderCell>
                        {
                            // @ts-ignore
                            <NameCell dataKey={employeeNameDataKey} />
                        }

                    </Column>
                    {isSequential && (
                        <Column
                            width={300}
                            resizable
                            key="scheduledDateAndTime"
                            sortable
                        >
                            <HeaderCell>Scheduled date and time</HeaderCell>
                            <Cell dataKey={sequentialEmployeeTurn} />
                        </Column>
                    )}
                    {
                        dataviewFields
                            .filter((x) => x.checked)
                            .map((element) => (
                                <Column
                                    key={element.key}
                                    width={160}
                                    resizable
                                    sortable
                                >
                                    <HeaderCell>{element.label}</HeaderCell>
                                    <Cell dataKey={element.key}>
                                        {
                                            /** @param {BidProfile.IEmployeeListSummaryRow} rowData */
                                            // @ts-ignore
                                            (rowData) => rowData.dataviewAttributes.find((x) => x.key === element.key)?.value
                                        }
                                    </Cell>
                                </Column>
                            ))
                    }
                </Table>
                <div className="pagination-wrapper">
                    <Pagination
                        layout={['total', '-', 'pager', 'skip', '|', 'limit']}
                        size="xs"
                        prev
                        next
                        first
                        last
                        ellipsis
                        boundaryLinks
                        total={employees.length}
                        limit={pageSize}
                        limitOptions={pageSizeOptions}
                        maxButtons={5}
                        activePage={pageIndex}
                        onChangePage={handleChangePageIndex}
                        onChangeLimit={handleChangePageSize}
                    />
                </div>
            </div>
        </div>
    );
}

export default EmployeeSelectionSummary;
